import { z } from 'zod';
import { DEFAULT_HEIGHT, DEFAULT_WIDTH, OUTPUT_FORMATS, TEMPLATES, THEMES } from './constants.js';

/**
 * SOURCE DE VÉRITÉ UNIQUE du contrat d'API.
 *
 * Ces schémas servent simultanément à :
 *   1. valider les requêtes entrantes dans le Worker (zValidator)
 *   2. dériver les types TypeScript (z.infer) — jamais de type écrit deux fois
 *   3. typer le SDK public
 *   4. servir de base à la future spécification OpenAPI
 *
 * Un changement ici casse le build de tous les consommateurs. C'est voulu.
 */

const hexColor = z
  .string()
  .transform((v) => (v.startsWith('#') ? v : `#${v}`))
  .pipe(z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Couleur hexadécimale à 6 chiffres attendue'))
  .transform((v) => v.toLowerCase());

/**
 * URL d'image distante.
 *
 * Écrit avec `refine` plutôt qu'avec `.url()` : les validateurs de format de
 * Zod ont changé de place entre la v3 et la v4, alors que `new URL()` est
 * stable. Le protocole est vérifié explicitement — accepter du `http://`
 * dégraderait la sécurité de la page qui affiche l'image.
 */
const httpsUrl = z
  .string()
  .max(2048)
  .refine(
    (value) => {
      try {
        return new URL(value).protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'Une URL HTTPS absolue est attendue.' },
  );

/**
 * Paramètres de GET /v1/og.
 *
 * Les alias courts (t, s, tpl) existent pour garder les URL lisibles dans le HTML.
 * Ils sont résolus AVANT le calcul de la clé de cache, de sorte que
 * `?t=Hello` et `?title=Hello` produisent exactement la même image et le même hash.
 */
export const ogParamsSchema = z
  .object({
    title: z.string().min(1).max(120).optional(),
    t: z.string().min(1).max(120).optional(),

    subtitle: z.string().max(200).optional(),
    s: z.string().max(200).optional(),

    template: z.enum(TEMPLATES).optional(),
    tpl: z.enum(TEMPLATES).optional(),

    theme: z.enum(THEMES).default('dark'),
    accent: hexColor.default('#6366f1'),

    author: z.string().max(60).optional(),
    a: z.string().max(60).optional(),

    tag: z.string().max(24).optional(),
    avatar: httpsUrl.optional(),
    logo: httpsUrl.optional(),

    /**
     * Visuel de couverture incrusté dans le SVG rendu.
     *
     * Distinct de `logo`, qui désigne une marque de petite taille, et de
     * `avatar`, qui désigne une personne : celui-ci occupe toute la colonne
     * illustrée du template `article`. HTTPS uniquement, 200 Ko maximum —
     * voir `lib/remote-image.ts` pour les contrôles appliqués.
     */
    image: httpsUrl.optional(),
    img: httpsUrl.optional(),

    w: z.coerce.number().int().min(600).max(2000).default(DEFAULT_WIDTH),
    h: z.coerce.number().int().min(315).max(1200).default(DEFAULT_HEIGHT),

    format: z.enum(OUTPUT_FORMATS).optional(),
    fmt: z.enum(OUTPUT_FORMATS).optional(),

    key: z.string().min(1),
  })
  .transform((p) => ({
    title: p.title ?? p.t,
    subtitle: p.subtitle ?? p.s,
    template: p.template ?? p.tpl ?? ('minimal' as const),
    theme: p.theme,
    accent: p.accent,
    author: p.author ?? p.a,
    tag: p.tag,
    avatar: p.avatar,
    logo: p.logo,
    image: p.image ?? p.img,
    width: p.w,
    height: p.h,
    format: p.format ?? p.fmt ?? ('png' as const),
    key: p.key,
  }))
  .refine((p) => p.title !== undefined, {
    message: "Le paramètre 'title' (ou son alias 't') est requis.",
    path: ['title'],
  });

export type OgParams = z.infer<typeof ogParamsSchema>;

/**
 * Sous-ensemble des paramètres qui influencent le rendu.
 * `key` en est volontairement exclu : deux utilisateurs différents
 * demandant la même image doivent partager la même entrée de cache.
 */
export type RenderParams = Omit<OgParams, 'key'>;

/* ------------------------------------------------------------------ */
/* API du dashboard                                                    */
/* ------------------------------------------------------------------ */

export const createApiKeySchema = z.object({
  name: z.string().min(1).max(50),
  type: z.enum(['public', 'secret']),
  allowedReferers: z.array(z.string().max(253)).max(20).default([]),
});
export type CreateApiKeyInput = z.infer<typeof createApiKeySchema>;

export const updateApiKeySchema = z
  .object({
    name: z.string().min(1).max(50).optional(),
    isActive: z.boolean().optional(),
    allowedReferers: z.array(z.string().max(253)).max(20).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Aucun champ à mettre à jour.' });
export type UpdateApiKeyInput = z.infer<typeof updateApiKeySchema>;

export const analyticsRangeSchema = z.object({
  range: z.enum(['7d', '30d', '90d', '12m']).default('30d'),
  keyId: z
    .string()
    .regex(/^[0-9a-f-]{36}$/i, 'Identifiant de clé invalide.')
    .optional(),
});
export type AnalyticsRange = z.infer<typeof analyticsRangeSchema>;

/* ------------------------------------------------------------------ */
/* Formes de réponse                                                   */
/* ------------------------------------------------------------------ */

export const apiKeyPublicSchema = z.object({
  id: z.string(),
  name: z.string(),
  prefix: z.string(),
  type: z.enum(['public', 'secret']),
  isActive: z.boolean(),
  allowedReferers: z.array(z.string()),
  lastUsedAt: z.number().nullable(),
  createdAt: z.number(),
});
export type ApiKeyPublic = z.infer<typeof apiKeyPublicSchema>;

export const usagePointSchema = z.object({
  date: z.string(),
  requests: z.number(),
  cacheHits: z.number(),
  renders: z.number(),
  errors: z.number(),
});
export type UsagePoint = z.infer<typeof usagePointSchema>;

export const usageSummarySchema = z.object({
  plan: z.enum(['free', 'pro', 'agency']),
  monthlyLimit: z.number(),
  used: z.number(),
  cacheHitRate: z.number(),
  periodStart: z.number(),
  periodEnd: z.number(),
  series: z.array(usagePointSchema),
});
export type UsageSummary = z.infer<typeof usageSummarySchema>;
